import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, shareReplay, switchMap } from 'rxjs';

import { BaseService } from './base-service';
import { enrichResult } from '../utils/errors';
import {
  CreateWorkPackageTypeRequest,
  UpdateWorkPackageTypeRequest,
  WorkPackageTypeDto,
  WorkPackageTypeDtoPage,
} from '@api/backend-connector/models';
import { WorkPackageTypesApi } from '@api/backend-connector/services';
import { wpBugIcon } from 'src/app/svg/wp-bug';
import { wpMilestoneIcon } from 'src/app/svg/wp-milestone';
import { wpPhaseIcon } from 'src/app/svg/wp-phase';
import { wpTaskIcon } from 'src/app/svg/wp-task';

export const WPTypeIconOptions = [wpBugIcon, wpTaskIcon, wpMilestoneIcon, wpPhaseIcon].map((icon) => icon.name);

const toasts = {
  createSuccessSummary: 'i18n.WorkPackageTypeToast.createSuccessSummary',
  createSuccessDetail: 'i18n.WorkPackageTypeToast.createSuccessDetail',
  createFailSummary: 'i18n.WorkPackageTypeToast.createFailSummary',
  createFailDetail: 'i18n.WorkPackageTypeToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.WorkPackageTypeToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.WorkPackageTypeToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.WorkPackageTypeToast.deleteFailSummary',
  deleteFailDetail: 'i18n.WorkPackageTypeToast.deleteFailDetail',
};

@Injectable({
  providedIn: 'root',
})
export class WorkPackageTypesService extends BaseService {
  private readonly workPackageTypeApi = inject(WorkPackageTypesApi);
  readonly toasts = toasts;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  listWorkPackageTypes(
    params: Parameters<typeof WorkPackageTypesService.prototype.workPackageTypeApi.listWorkPackageTypes>[0],
  ): Observable<WorkPackageTypeDtoPage> {
    return this.workPackageTypeApi.listWorkPackageTypes(params);
  }

  allWorkPackageTypes(): Observable<WorkPackageTypeDto[]> {
    // FIXME check page count
    return this.listWorkPackageTypes({ sorting: ['position'] }).pipe(map(({ items }) => items));
  }

  readonly wpTypes$ = this.refresh$.pipe(
    switchMap(() => this.allWorkPackageTypes()),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  readonly wpTypesMap$ = this.wpTypes$.pipe(map((entries) => new Map(entries.map((t) => [t.id, t]))));

  // FIXME check page count
  private allCreateProjectWorkPackageTypes$ = this.refresh$.pipe(
    switchMap(() =>
      this.listWorkPackageTypes({ sorting: ['position'], isActiveOnNewProjects: true }).pipe(map(({ items }) => items)),
    ),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  allCreateProjectWorkPackageTypes(): Observable<WorkPackageTypeDto[]> {
    return this.allCreateProjectWorkPackageTypes$;
  }

  getWorkPackageType(params: { id: string }): Observable<WorkPackageTypeDto> {
    return this.workPackageTypeApi.getWorkPackageType({
      ...params,
    });
  }

  createWorkPackageType(request: CreateWorkPackageTypeRequest) {
    return this.workPackageTypeApi.createWorkPackageType({ body: request }).pipe(enrichResult());
  }

  updateWorkPackageType(id: string, body: UpdateWorkPackageTypeRequest) {
    return this.workPackageTypeApi.updateWorkPackageType({ id, body }).pipe(enrichResult());
  }

  deleteWorkPackageType(params: { id: string }) {
    return this.workPackageTypeApi.deleteWorkPackageType(params);
  }
}
